import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type SvcService, useSetProjectSuspension, useSetServiceSuspension } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import {
  normalizeReason,
  REASON_MAX,
  reasonProblem,
  serviceList,
  serviceNoun,
} from "@/lib/services-gateway";

/** Which suspension the dialog changes, and which way. */
export interface SuspensionTarget {
  projectId: number;
  projectName: string;
  /** One service of the project, or `null` for all of it. */
  service: SvcService | null;
  /** Suspend it, or resume what is suspended at exactly this scope. */
  suspend: boolean;
  /**
   * The project's services its app calls without the gateway (models direct,
   * its own Langfuse): a stop does not reach them. Empty for one service --
   * suspending one of those is refused.
   */
  offGateway: SvcService[];
}

/**
 * Suspend or resume one service of a project, or the whole project. Admin only.
 * Suspending needs a reason the project's owners read; nothing is revoked, so
 * resuming brings the same keys back.
 */
export function SuspendDialog({
  target,
  onOpenChange,
}: {
  target: SuspensionTarget | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {target && (
          // Remount per target so a reason never carries into another one.
          <SuspendForm
            key={`${target.projectId}-${target.service ?? "project"}-${target.suspend}`}
            target={target}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuspendForm({ target, onClose }: { target: SuspensionTarget; onClose: () => void }) {
  const serviceChange = useSetServiceSuspension(target.projectId);
  const projectChange = useSetProjectSuspension(target.projectId);
  const [text, setText] = useState("");
  const reason = normalizeReason(text);
  // A reason is required to suspend; to resume it is optional, but checked if given.
  const problem = target.suspend || reason ? reasonProblem(reason) : null;
  const pending = serviceChange.isPending || projectChange.isPending;
  const what = target.service ? serviceNoun(target.service) : "every AI service";
  const verb = target.suspend ? "Suspend" : "Resume";

  const handleSubmit = async () => {
    if (problem !== null || pending) {
      return;
    }
    const body = { suspended: target.suspend, reason: reason || null };
    try {
      const result = target.service
        ? await serviceChange.mutateAsync({ service: target.service, ...body })
        : await projectChange.mutateAsync(body);
      const done = target.suspend ? "suspended" : "resumed";
      toast.success(
        result?.changed === false
          ? `Already ${done}.`
          : `${target.projectName}: ${what} ${done}. It takes effect within about a minute.`,
      );
      const missed = target.suspend ? (result?.off_gateway_services ?? []) : [];
      if (missed.length > 0) {
        toast.warning(`Not stopped: ${serviceList(missed)} (not through the gateway).`);
      }
      onClose();
    } catch (err) {
      logger.error(`[services] ${verb.toLowerCase()} failed:`, err);
      // A toast, not a line in the form: the dialog may have been closed meanwhile.
      toast.error(readApiErrorMessage(err, `Could not ${verb.toLowerCase()}. Please try again.`));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {verb} {what} of {target.projectName}?
        </DialogTitle>
        <DialogDescription>
          {target.suspend
            ? "The app's calls are refused (suspended) within about a minute, until an admin resumes them. No key is revoked or rotated, and nothing is redeployed. The project's owners see your reason."
            : "The same keys work again within about a minute. Nothing is redeployed."}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="space-y-1.5">
          {target.suspend && target.offGateway.length > 0 && (
            <p className="pb-2 text-xs text-amber-700 dark:text-amber-400">
              This does not stop its {serviceList(target.offGateway)}: the app calls{" "}
              {target.offGateway.length === 1 ? "it" : "them"} without the gateway.
            </p>
          )}
          <Label htmlFor="suspension-reason">
            {target.suspend ? "Reason (the owners read it)" : "Reason (optional, logged)"}
          </Label>
          <Textarea
            id="suspension-reason"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={REASON_MAX + 100}
            rows={3}
            placeholder={
              target.suspend ? "e.g. A runaway loop is spending the model budget" : undefined
            }
          />
          {text.trim() !== "" && problem && (
            <p className="text-muted-foreground text-xs">{problem}</p>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant={target.suspend ? "destructive" : "default"}
          disabled={problem !== null || pending}
          onClick={() => void handleSubmit()}
        >
          {pending ? (target.suspend ? "Suspending…" : "Resuming…") : verb}
        </Button>
      </DialogFooter>
    </>
  );
}
